import { createSlice, createSelector } from "@reduxjs/toolkit";

const initialState = {
  isLoggedIn: false,
  user: null,
  hasExpiredSubscription: false, // New flag for subscription status
  overChecks: {
    usersExceeded: false, // New flag for user limit
    workflowsExceeded: false, // New flag for workflow limit
    executionsExceeded: false,
  },
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    login: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
    },
    logout: (state) => {
      state.isLoggedIn = false;
      state.user = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    assignProject: (state, action) => {
      if (state.user && state.user.projectsAssigned) {
        state.user.projectsAssigned.push(action.payload);
      }
    },
    setSubscriptionStatus: (state, action) => {
      state.hasExpiredSubscription = action.payload;
    },
    // Plan limits are off for now, so nothing is ever over its limit. Restore
    // reading the payload (users, workflows, executions) to switch them back on.
    setOverLimitChecks: (state) => {
      state.overChecks.usersExceeded = false;
      state.overChecks.workflowsExceeded = false;
      state.overChecks.executionsExceeded = false;
    },
  },
});

// 1. Basic selector to get the company data
const selectCompanyData = (state) => state.user.user?.companyData;

// 2. Memoized selector using createSelector
export const getPlanDetails = createSelector(
  [selectCompanyData], // Input: Only look at companyData
  (companyData) => {
    // This function only runs if companyData actually changes
    if (!companyData) {
      return {
        plan: "Free",
        maxUsers: 2,
        currentUsers: 0,
      };
    }

    const { subscription } = companyData;

    return {
      plan: subscription?.plan || "Free",
      maxUsers: subscription.limits?.maxUsers || 2,
      currentUsers: subscription.usage?.usersCount || 0,
      maxWorkflows: subscription.limits.maxSavedWorkflows || 0,
      currentWorkflows: subscription.usage?.savedWorkflows || 0,
    };
  },
);

export const getOverLimitChecks = (state) => state.user.overChecks;

export const {
  login,
  logout,
  updateUser,
  assignProject,
  setSubscriptionStatus,
  setOverLimitChecks,
} = userSlice.actions;

export default userSlice.reducer;
